<section
  class="mx-auto w-full max-w-360 p-6 2xl:px-0 flex-1 flex flex-col"
  aria-labelledby="md-page-heading"
>
  <!-- Header, filters and charts -->
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div
      class="flex w-full flex-wrap items-start justify-between gap-4 md:flex-nowrap lg:gap-12"
    >
      <div class="min-w-0">
        <a
          routerLink="/analytics/dashboard"
          class="mb-1 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-black"
          aria-label="Back to IQ Analytics"
        >
          IQ Analytics
        </a>
        <h1
          id="md-page-heading"
          class="m-0 flex items-center gap-2 font-montserrat text-3xl font-bold leading-tight text-gray-900 [text-wrap:auto]"
        >
          Metric Details
        </h1>
        <p class="mt-1 text-sm text-gray-400">
          Explore metric trends by device, connection, and dimension for the selected time frame.
        </p>
      </div>

      @if (canShowSampleDataToggle()) {
        <div class="flex flex-wrap items-center gap-2">
          <app-toggle
            [disabled]="loading"
            [(ngModel)]="showMockData"
            (ngModelChange)="toggleMockData($event)"
            label="Sample Data"
            size="md"
            color="primary"
          />
        </div>
      }
    </div>

    <!-- Controls -->
    <div
      class="iq-controls grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
    >
      <app-sorting-dropdown (selectedOptionChange)="onSortingChange($event)" />
      <app-select-dimension-dropdown
        (selectedOptionChange)="onDimensionChange($event)"
      />
      <app-analytics-time-selector
        (selectedOptionChange)="onChildSelectionChange($event)"
        label="Activation Time"
      />
    </div>

    @if (selectedDimension === 'connection') {
      <div
        class="flex flex-wrap justify-end gap-1"
        role="group"
        aria-label="Metric type"
      >
        @for (btn of metricButtons; track btn.value) {
          <button
            type="button"
            (click)="metricType = btn.value; onMetricChange()"
            class="inline-flex cursor-pointer select-none items-center border-b-4 px-3 pb-1 pt-0.5 font-lato text-sm text-gray-700 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            [ngClass]="[
              btn.borderClass,
              metricType === btn.value
                ? 'font-semibold opacity-100'
                : 'font-normal opacity-70',
            ]"
            [attr.aria-pressed]="metricType === btn.value"
          >
            {{ btn.label }}
          </button>
        }
      </div>
    }

    <!-- Charts (rendered for every view regardless of the selected filter) -->
    @if (loading) {
      <div
        class="flex flex-col items-center justify-center py-12 text-center text-gray-600"
        role="status"
        aria-live="polite"
      >
        <mat-spinner [diameter]="48"></mat-spinner>
        <p class="mt-4 font-lato text-sm text-gray-500">
          Loading metrics data...
        </p>
      </div>
    } @else if (isNoContent) {
      <div
        class="flex flex-col items-center justify-center py-12 text-center text-gray-700"
        role="status"
        aria-live="polite"
      >
        <mat-icon
          fontSet="material-icons-outlined"
          class="mb-2 !text-5xl text-gray-300"
          aria-hidden="true"
          >insights</mat-icon
        >
        <h2 class="font-montserrat text-lg font-semibold">No Data Available</h2>
        <p class="mt-1 max-w-md font-lato text-sm text-gray-500">
          No metrics data available for the selected criteria. Please adjust
          your filters or dimensions.
        </p>
      </div>
    } @else {
      <div class="grid w-full min-w-0 grid-cols-1 gap-[1px] overflow-hidden bg-gray-150 shadow-sm md:grid-cols-2">
        <div
          class="flex min-w-0 flex-col bg-white p-4 lg:p-5"
          role="img"
          [attr.aria-label]="
            (selectedMetricLabel || 'Metric') + ' bar chart by device'
          "
        >
          <h2
            class="m-0 mb-0 flex items-center gap-1.5 font-montserrat text-base font-semibold uppercase tracking-wider text-gray-900"
          >
            <span class="material-icons-outlined text-[16px]" aria-hidden="true"
              >bar_chart</span
            >
            {{ selectedMetricLabel }}
          </h2>
          <app-analytics-widget
            [isNoContent]="isNoContent"
            [loading]="loading"
            class="h-full w-full"
          >
            <app-horizontal-bar-graph
              [data]="sortedMetricsData"
              [categories]="metricCategories"
              [chartName]="selectedMetricLabel"
              (changeDevice)="onChangeDevice($event)"
            />
          </app-analytics-widget>
        </div>
        <div
          class="flex min-w-0 flex-col bg-white p-4 lg:p-5"
          role="img"
          [attr.aria-label]="
            (selectedDeviceChartLabel || 'Metric') + ' trend over time'
          "
        >
          <h2
            class="m-0 mb-0 flex items-center gap-1.5 font-montserrat text-base font-semibold uppercase tracking-wider text-gray-900"
          >
            <span class="material-icons-outlined text-[16px]" aria-hidden="true"
              >show_chart</span
            >
            {{ selectedDeviceChartLabel }}
          </h2>
          <app-analytics-widget
            [isNoContent]="isNoContent"
            [loading]="loading"
            class="h-full w-full"
          >
            <app-trend-histogram
              [categories]="historicalCategories"
              [chartName]="selectedDeviceChartLabel"
            />
          </app-analytics-widget>
        </div>
      </div>
    }
  </div>

  <!-- Table -->
  <div class="scrollbar-thin mt-4 w-full overflow-x-auto">
    <app-metric-details
      [dataSource]="unmTableData"
      [loading]="tableLoading"
      [isNoContent]="isNoTableContent"
      [tableDimension]="selectedDimension"
      class="min-w-[40rem] md:min-w-0"
    />
  </div>
</section>

